// src/components/common/MessageParser.jsx
import React, { useState, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CollapsedCodeCard from '@/components/layout/ChatColumn/CollapsedCodeCard';
import CollapsedDocumentCard from '@/components/layout/ChatColumn/CollapsedDocumentCard';
import { FileText, Loader2, Mic, Image as ImageIcon } from 'lucide-react';
import { ImageViewer, FileViewer } from './ImageAndFileViewer';
import ToolStatusIndicator from './ToolStatusIndicator';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff', 'tif', 'ico', 'heic', 'heif'];

const isImageFile = (file) => {
    if (!file) return false;
    const mimeType = file.type || file.mimeType || '';
    if (mimeType.startsWith('image/')) return true;
    
    const extension = file.name ? file.name.split('.').pop()?.toLowerCase() : '';
    return extension && IMAGE_EXTENSIONS.includes(extension);
};

/* ========================
 * filterDevContent - Filters out user-clipboard metadata from display.
 * This is a UI-only filter to hide debug/context markers from the chat bubble.
 * ======================== */
const filterDevContent = (content) => {
    if (typeof content !== 'string') return content;
    const regex = /==== USER HAS ATTACHED FILES TO CURRENT MESSAGE - READ EVERY LINE BEFORE RESPONDING ====[\s\S]*?==== USER'S MESSAGE STARTS HERE ====/g;
    
    let filteredContent = content.replace(regex, '');
    
    return filteredContent.trim();
};


/* ========================
 * TranscriptionBlock - Renders an audio transcription with status indicator
 * Displays a loading animation if transcribing, otherwise a microphone icon.
 * ======================== */
const TranscriptionBlock = ({ content, fontSize = 'md' }) => {
    const isLoading = content === 'Transcribing...';
    return (
      <div 
        className="my-2 pl-3 pr-2 py-2 border-l-4 rounded-r-md"
        style={{ borderColor: isLoading ? 'var(--on-surface-dim)' : 'var(--transcript-border-color)' }}
      >
        <div className="flex items-start gap-2.5">
          {isLoading ? (
            <Loader2 size={18} className="text-on-surface-dim flex-shrink-0 mt-0.5 animate-spin" />
          ) : (
            <Mic size={18} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--transcript-icon-color)' }} />
          )}
          <p className={`text-${fontSize} italic break-words leading-normal ${isLoading ? 'text-on-surface-dim animate-pulse' : 'text-user-bubble-text/90'}`}>
            {content}
          </p>
        </div>
      </div>
    );
};

const MessageParser = React.memo(({
    contentBlocks = [],
    messageId,
    attachments = [],
    isUserMessage = false,
    onCodeBlockClick,
    fontSize = 'md',
    isLoadingOrStreaming
}) => {
    const [expandedImage, setExpandedImage] = useState(null);
    const [expandedFile, setExpandedFile] = useState(null);
    const codeBlockRenderIndex = useRef(0);
    
    const { imageFiles, otherFiles } = useMemo(() => {
        const images = [];
        const others = [];
        for (const file of attachments) {
            if (!file) continue;
            if (isImageFile(file)) {
                images.push(file);
            } else if (!file.type?.startsWith('audio/')) {
                others.push(file);
            }
        }
        return { imageFiles: images, otherFiles: others };
    }, [attachments]);

    const markdownComponentsForTextParts = useMemo(() => ({
        // Override pre for code blocks (```code```)
        pre: ({ node, children, ...props }) => {
            const codeElement = React.Children.toArray(children)[0];
            
            if (
                React.isValidElement(codeElement) &&
                codeElement.props &&
                typeof codeElement.props.children === 'string' &&
                typeof codeElement.props.className === 'string' &&
                codeElement.props.className.startsWith('language-')
            ) {
                const { className } = codeElement.props;
                const codeString = String(codeElement.props.children).replace(/\n$/, '');
                const match = /language-(\w+)/.exec(className);
                const lang = match ? match[1] : 'plaintext';
                
                codeBlockRenderIndex.current += 1;
                const currentBlockIndex = codeBlockRenderIndex.current;

                return (
                    <CollapsedCodeCard
                        code={codeString}
                        language={lang}
                        messageId={messageId}
                        blockIndex={currentBlockIndex}
                        isStreaming={isLoadingOrStreaming}
                    />
                );
            }
            return <pre {...props} className="whitespace-pre-wrap">{children}</pre>;
        },
        // Override code for inline code (`code`)
        code: ({ node, inline, className, children, ...props }) => {
            if (inline) {
                 return <code className="text-sm bg-surface-dark/80 text-on-surface py-0.5 px-1.5 rounded-md font-mono border border-border-subtle/50">{children}</code>;
            }
            return <code className={`${className} whitespace-pre-wrap`} {...props}>{children}</code>;
        },
        img: ({node, ...props}) => {
            return (
                <img 
                    {...props} 
                    className="max-w-full h-auto rounded-lg my-2 cursor-pointer hover:ring-2 hover:ring-accent/50 transition-all" 
                    onClick={() => setExpandedImage({ storageUrl: props.src, name: props.alt })}
                />
            );
        },
    }), [setExpandedImage, messageId, isLoadingOrStreaming]);

    const showThinkingPlaceholder = isLoadingOrStreaming && contentBlocks.length === 0;

    let localDocBlockRenderIndex = 0;
    codeBlockRenderIndex.current = 0;

    return (
        <div className="space-y-2">
            <div className="space-y-4">
                {showThinkingPlaceholder && (
                    <div className={`text-${fontSize} ${isUserMessage ? 'text-user-bubble-text' : 'text-agent-bubble-text'} animate-pulse`}>
                        Thinking...
                    </div>
                )}

                {contentBlocks.map((part, idx) => {
                    const key = `${part.type}-${messageId}-${part.id || idx}`;
                    switch (part.type) {
                        case 'image':
                            if (!part.source?.url) return null;
                            return (
                                <img
                                    key={key}
                                    src={part.source.url}
                                    alt={part.source.alt || 'Generated Image'}
                                    className="max-w-full h-auto rounded-lg my-2 cursor-pointer hover:ring-2 hover:ring-accent/50 transition-all"
                                    onClick={() => setExpandedImage({ storageUrl: part.source.url, name: part.source.alt || 'Generated Image' })}
                                />
                            );
                        case 'transcription':
                            return (
                                <TranscriptionBlock
                                    key={key}
                                    content={part.content}
                                    fontSize={fontSize}
                                />
                            );
                        case 'text':
                            if (part.isStreamingImage) {
                                return (
                                    <div key={key} className={`text-${fontSize} ${isUserMessage ? 'text-user-bubble-text' : 'text-agent-bubble-text'} p-2 rounded-md bg-white/5`}>
                                        <div className="flex items-center gap-2 animate-pulse">
                                            <ImageIcon size={16} className="text-white/60" />
                                            <span className="text-sm text-white/60">Generating image...</span>
                                        </div>
                                    </div>
                                );
                            }
                            const finalContent = isUserMessage ? filterDevContent(part.content) : part.content;
                            if (!finalContent || finalContent.trim() === '') return null;
                            return (
                                <div key={key} className={`text-${fontSize} ${isUserMessage ? 'text-user-bubble-text' : 'text-agent-bubble-text'} markdown-content break-words`}>
                                    <ReactMarkdown components={markdownComponentsForTextParts} remarkPlugins={[remarkGfm]}>
                                        {finalContent}
                                    </ReactMarkdown>
                                </div>
                            );
                        case 'code': // This case is for historical messages with pre-parsed code blocks
                            codeBlockRenderIndex.current += 1;
                            return (
                                <CollapsedCodeCard
                                    key={key}
                                    code={part.content}
                                    language={part.language}
                                    messageId={messageId}
                                    blockIndex={codeBlockRenderIndex.current} 
                                    onClick={() => onCodeBlockClick?.(part.content, part.language)}
                                    isStreaming={part.isStreaming}
                                />
                            );
                        case 'document':
                            localDocBlockRenderIndex++;
                            return (
                                <CollapsedDocumentCard
                                    key={key}
                                    content={part.content}
                                    messageId={messageId}
                                    blockIndex={localDocBlockRenderIndex}
                                    isStreaming={isLoadingOrStreaming && !part.isTruncated && part.content.length === 0}
                                />
                            );
                        case 'tool_code':
                            return (
                                <ToolStatusIndicator
                                    key={key}
                                    toolName={part.toolName}
                                    status={part.status}
                                    resultPreview={part.result}
                                    toolArgs={part.toolArgs}
                                    isLoadingOrStreaming={isLoadingOrStreaming}
                                />
                            );
                        default:
                            return null;
                    }
                })}
            </div>

            {imageFiles.length > 0 && (
                <div className="grid grid-cols-1 gap-4 mt-4">
                    {imageFiles.map((file, index) => (
                        <div
                            key={`image-${messageId}-${file.id || index}`}
                            className="relative rounded-lg overflow-hidden cursor-pointer max-w-[400px]"
                            onClick={() => setExpandedImage(file)}
                        >
                            <img
                                src={file.storageUrl || file.preview}
                                alt={file.name || 'Attached Image'}
                                className="w-full h-auto rounded-lg max-h-[360px] object-contain bg-surface-dark/50"
                            />
                            <div className="absolute bottom-2 left-2 text-xs bg-surface-dark/80 text-white border-white/10 backdrop-blur-sm px-2 py-1 rounded-md border">
                                {file.name || 'Attached Image'}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {otherFiles.length > 0 && (
                <div className={`mt-4 pt-4 border-t border-white/10`}>
                    <div className="text-sm mb-2 text-white/80">
                        Attached Files
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                        {otherFiles.map((file, index) => (
                            <button
                                key={`file-${messageId}-${file.id || index}`}
                                onClick={() => setExpandedFile(file)}
                                className="w-full text-left bg-surface/40 hover:bg-surface/60 backdrop-blur-sm rounded-lg transition-all border border-white/10 shadow-glass-sm"
                            >
                                <div className="flex items-center gap-3 p-3">
                                    <div className="p-2 rounded-lg bg-white/5">
                                        <FileText size={16} className="text-on-surface" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate text-on-surface">
                                            {file.name}
                                        </div>
                                        <div className="text-xs truncate text-on-surface">
                                            {`${(file.size / 1024).toFixed(1)} KB`}
                                        </div>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {expandedImage && (<ImageViewer src={expandedImage.storageUrl || expandedImage.preview} alt={expandedImage.name} onClose={() => setExpandedImage(null)} />)}
            {expandedFile && (<FileViewer file={expandedFile} onClose={() => setExpandedFile(null)} />)}
        </div>
    );
});

export default MessageParser;